import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useTheme } from "styled-components/native";
import { useTranslation } from "react-i18next";
import { ProductCategory } from "@utils/constants";
import { getProductColor, getProductAlphaColor } from "../utils/utils";
import { NavigationHistoryState } from "@components/molecules/Breadcrumbs/utils";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { replaceDeeplinkScheme } from "@utils/product/productValidators";
import { Linking, Platform } from "react-native";

interface UsePdpComputedValuesProps {
  product_type: ProductCategory;
  itemDetail: any;
  isModal: boolean;
  enableJewelryBuilder: boolean;
  enterSecondFlow: boolean;
  mItem: any;
  mStoneItemDetail: any;
  customOptions: any[];
  isStockNumberVisible: boolean;
  getAddToJewelryOptionsData: any;
}

export const usePdpComputedValues = ({
  product_type,
  itemDetail,
  isModal,
  enableJewelryBuilder,
  enterSecondFlow,
  mItem,
  mStoneItemDetail,
  customOptions,
  isStockNumberVisible,
  getAddToJewelryOptionsData,
}: UsePdpComputedValuesProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const userInfo = useUserInfo();
  const { history } = useSelector(
    (state: any) => state.navigationHistory as NavigationHistoryState
  );

  // Get jewelry style (for earring detection)
  const jewelryStyle = useMemo(
    () =>
      enterSecondFlow === true
        ? mStoneItemDetail?.jewelry_style
        : mItem?.jewelry_style,
    [enterSecondFlow, mStoneItemDetail?.jewelry_style, mItem?.jewelry_style]
  );

  const isEarring = useMemo(
    () => jewelryStyle?.includes("Earring") ?? false,
    [jewelryStyle]
  );

  // Get product colors
  const productColor = useMemo(
    () => getProductColor(product_type),
    [product_type]
  );

  const productAlphaColor = useMemo(
    () => getProductAlphaColor(product_type),
    [product_type]
  );

  // Filter function for custom options
  const getFilterDetailData = useMemo(
    () =>
      (searchText: string): boolean => {
        const filteredArray = customOptions.filter(
          (item) => item.name === searchText
        );
        return filteredArray.length === 0;
      },
    [customOptions]
  );

  const isCertificateShow = useMemo(
    () => getFilterDetailData("certificate"),
    [getFilterDetailData]
  );

  const allowBreadcrumbByProduct = useMemo(() => {
    return product_type !== ProductCategory.JEWELRY
      ? getAddToJewelryOptionsData?.data?.add_to_jewelry?.options.length > 0 ||
          enterSecondFlow
      : itemDetail?.is_customizable === "jewelry_builder";
  }, [product_type, getAddToJewelryOptionsData, enterSecondFlow, itemDetail]);

  // Show breadcrumb in mobile
  const showBreadCrumbInMobile = useMemo(
    () =>
      !isModal &&
      enableJewelryBuilder &&
      history.length >= 2 &&
      theme.isMobile &&
      allowBreadcrumbByProduct &&
      !(itemDetail?.company_identifier && userInfo.companyIdentifierEnabled()),
    [
      isModal,
      enableJewelryBuilder,
      history.length,
      theme.isMobile,
      getAddToJewelryOptionsData,
    ]
  );

  // Linked pair comments HTML
  const LinkedPairComments = useMemo(() => {
    if (!itemDetail?.children) return "-";

    return (
      itemDetail?.children
        ?.map((child: any, index: number, array: any[]) => {
          const stock = child?.stock_num ?? "-";
          const comment =
            child?.item_comment === "" ? "-" : child?.item_comment ?? "-";

          const stockDiv = isStockNumberVisible
            ? `
            <div style="font-family: Roboto; font-weight: 400; font-size: 14px; line-height: 20px; color:${
              theme["core-06"]
            }">
              ${t("productDetailPage.stockNum.pair.text", "Stock#")}: ${stock}
            </div>
          `
            : "";

          const spacer =
            index < array.length - 1 ? `<div style="height: 16px;"></div>` : "";

          return `
          <div>
            ${stockDiv}
            <div style="font-family: Roboto; font-weight: 500; font-size: 14px; line-height: 20px; color:${theme["core-05"]}">
              ${comment}
            </div>
          </div>
          ${spacer}
        `;
        })
        .join("") || "-"
    );
  }, [itemDetail?.children, isStockNumberVisible, theme, t]);

  const renderersProps = useMemo(
    () => ({
      a: {
        onPress(...args: any[]) {
          const href = args[1];
          if (href && Platform.OS !== "web") {
            const deepLink = replaceDeeplinkScheme(
              href,
              process.env.EXPO_PUBLIC_APP_SCHEME
            );
            Linking.openURL(deepLink);
          } else {
            Linking.canOpenURL(href).then(() => {
              Linking.openURL(href);
            });
          }
        },
      },
    }),
    []
  );

  return {
    jewelryStyle,
    isEarring,
    productColor,
    productAlphaColor,
    getFilterDetailData,
    isCertificateShow,
    allowBreadcrumbByProduct,
    showBreadCrumbInMobile,
    LinkedPairComments,
    renderersProps,
  };
};
